import type { OutboxRow } from "../../../server/core/send";
import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { chip } from "../../ui/record";
import { firstPageParams, list, type Column } from "../../ui/list";
import type { PageInfo } from "../../../server/rules/list-page";
import { openSend } from "../../ui/send";
import { uploadFile } from "../../ui/upload";
import { pageHead } from "../empty";

// Inbox › Outbox (brief 1B-4, mockup inbox/outbox): everything that left, emails, texts, faxes
// and paper mail, with how, to, when, status, tracking and cost. Tabs filter by channel, the
// select by status. A row opens what happened to it, and a secure link can be turned off there.
// Upload to mail mails any PDF you upload (the Send sheet opens on Mail).

const FILTERS = ["All", "Mail", "Fax", "Email", "Text"] as const;

type Answer = PageInfo & {
  rows: OutboxRow[];
  canEdit: boolean;
  seesMoney: boolean;
  channels: Record<string, number>;
};

export async function outboxScreen(host: HTMLElement, ctx: ScreenContext) {
  let channel: (typeof FILTERS)[number] = "All";
  let status = "";
  // Brief 1E-34: Person or record narrows the list to what went to one person, patient or client.
  let person = "";
  const listId = "inbox/outbox";
  // Brief 1E-32: the server answers one page at a time; the tabs and the status pick go with it.
  const extra = (): Record<string, string> => ({
    ...(channel === "All" ? {} : { "p.channel": channel }),
    ...(status ? { "p.status": status } : {}),
    ...(person.trim() ? { "f.record": person.trim() } : {}),
  });
  const ask = (params: URLSearchParams) => {
    for (const [k, v] of Object.entries(extra())) params.set(k, v);
    return api<Answer>("GET", `/api/outbox?${params.toString()}`);
  };
  const res = await ask(firstPageParams(listId, ctx.me.user.id));
  if (!res.ok) {
    host.append(
      pageHead(ctx),
      el("p", { class: "note", role: "alert" }, "The Outbox couldn't be loaded."),
    );
    return;
  }
  let counts = res.body;
  const canEdit = res.body.canEdit;
  // The Cost column shows only to roles with the Money switch (brief 1D-5).
  const seesMoney = res.body.seesMoney;

  const reload = async () => {
    await table.reload();
  };

  const actions: Node[] = [];
  if (canEdit) {
    const picker = el("input", { type: "file", id: "ob-up", accept: ".pdf", hidden: true });
    const up = el("label", { class: "btn pri", for: "ob-up" }, icon("upload"), " Upload to mail");
    picker.addEventListener("change", async () => {
      const file = picker.files?.[0];
      picker.value = "";
      if (!file) return;
      toast(`Uploading ${file.name}…`);
      const done = await uploadFile(file, { kind: "outbox", id: ctx.me.company.id });
      if (!done.ok) return toast("That file wasn't uploaded", done.error);
      await openSend({
        files: [done.file],
        document: done.file.name,
        tab: "Mail",
        onSent: () => void reload(),
      });
    });
    actions.push(up, picker);
  }
  host.append(
    pageHead(
      ctx,
      "Everything that left the building: emails, texts, faxes and paper mail, with status and tracking.",
      actions,
    ),
  );
  const tabs = el("div", { class: "tabs", role: "tablist", "aria-label": "Channel" });
  const pick = el("select", { id: "ob-st", "aria-label": "Status" });
  pick.addEventListener("change", () => {
    status = pick.value;
    void table.reload(true);
  });
  const who = el("input", {
    id: "ob-q",
    type: "search",
    placeholder: "Aisha Rahman, Robert Ellison",
    autocomplete: "off",
  });
  let typing: ReturnType<typeof setTimeout> | undefined;
  who.addEventListener("input", () => {
    clearTimeout(typing);
    typing = setTimeout(() => {
      person = who.value;
      void table.reload(true);
    }, 250);
  });
  const bar = el(
    "div",
    {
      class: "row",
      style: "justify-content:space-between;align-items:flex-end;flex-wrap:wrap;gap:8px",
    },
    tabs,
    el(
      "div",
      { class: "row", style: "gap:8px;align-items:flex-end;flex-wrap:wrap" },
      el(
        "div",
        { class: "field", style: "margin:0;max-width:320px", "data-unsaved": "off" },
        el("label", { for: "ob-q" }, "Person or record"),
        who,
      ),
      el("div", { class: "field", style: "margin:0" }, pick),
    ),
  );

  function details(o: OutboxRow) {
    const off = el(
      "button",
      { type: "button", class: "btn" },
      icon("circle-slash"),
      " Turn off the link",
    );
    off.addEventListener("click", async () => {
      off.disabled = true;
      const r = await api<Answer>("POST", `/api/outbox/${o.id}/revoke`, {});
      if (!r.ok) {
        off.disabled = false;
        return toast("The link wasn't turned off", r.body.error);
      }
      m.close();
      await reload();
      toast("Link turned off", "It doesn't open any more");
    });
    const openRec = o.record?.path
      ? el("button", { type: "button", class: "btn" }, `Open ${o.record.name}`)
      : null;
    openRec?.addEventListener("click", () => {
      m.close();
      if (o.record?.path) ctx.go(o.record.path);
    });
    const m = openModal({
      title: o.document,
      sub: `${o.channel} · ${o.how} · to ${o.to}`,
      body: [
        el(
          "p",
          {},
          chip(o.status, o.level),
          o.error ? ` ${o.error}` : "",
          o.returned ? ` ${o.returned}` : "",
        ),
        el(
          "div",
          {},
          o.link
            ? el(
                "p",
                { class: "small" },
                o.link.revoked
                  ? "The secure link was turned off."
                  : `Secure link ${o.link.live ? `works until ${o.link.until}` : "has expired"}. Opened ${o.link.opened} time${o.link.opened === 1 ? "" : "s"}${o.link.openedAt ? `, first ${o.link.openedAt}` : ""}.`,
              )
            : null,
        ),
        el(
          "ol",
          { class: "actlist" },
          ...o.history.map((h) =>
            el(
              "li",
              {},
              el("b", {}, h.status),
              ` · ${h.when}`,
              h.note ? el("div", { class: "small muted" }, h.note) : null,
            ),
          ),
        ),
      ],
      actions: [...(o.canRevoke ? [off] : []), ...(openRec ? [openRec] : [])],
    });
  }

  /** The channel tabs and the status pick, with counts over everything that left. */
  function drawBar() {
    tabs.replaceChildren(
      ...FILTERS.map((c) => {
        const b = el(
          "button",
          { type: "button", role: "tab", "aria-selected": String(channel === c) },
          `${c} `,
          el("span", { class: "muted" }, String(counts.channels[c] ?? 0)),
        );
        b.addEventListener("click", () => {
          channel = c;
          drawBar();
          void table.reload(true);
        });
        return b;
      }),
    );
    pick.replaceChildren(
      el("option", { value: "" }, "All statuses"),
      ...(counts.values.status ?? []).map((s) =>
        el("option", { value: s, selected: s === status }, s),
      ),
    );
  }

  const columns: Column<OutboxRow>[] = [
    {
      id: "document",
      label: "Document",
      text: (o) => o.document,
      search: true,
      cell: (o) =>
        el(
          "span",
          {},
          el("b", {}, o.auto ? `Automatic · ${o.auto}` : o.document),
          o.record ? el("div", { class: "small muted" }, o.record.name) : null,
        ),
    },
    {
      id: "channel",
      label: "How",
      text: (o) => o.channel,
      cell: (o) => el("span", {}, `${o.channel} · `, el("span", { class: "small muted" }, o.how)),
    },
    { id: "to", label: "To", text: (o) => o.to, search: true },
    { id: "sent", label: "Sent", text: (o) => o.sent },
    { id: "status", label: "Status", text: (o) => o.status, cell: (o) => chip(o.status, o.level) },
    {
      id: "tracking",
      label: "Tracking",
      text: (o) => o.tracking,
      cell: (o) => el("span", { class: "small" }, o.tracking),
    },
    ...(seesMoney
      ? [{ id: "cost", label: "Cost", num: true, text: (o: OutboxRow) => o.cost }]
      : []),
  ];
  const table = list<OutboxRow>({
    id: listId,
    userId: ctx.me.user.id,
    noun: "sent",
    caption: "Outbox",
    searchLabel: "Search documents or who it went to",
    first: { rows: res.body.rows, page: res.body },
    extra,
    source: async (params) => {
      const r = await ask(params);
      if (!r.ok) return null;
      counts = r.body;
      drawBar();
      return { rows: r.body.rows, page: r.body };
    },
    rowId: (o) => o.id,
    columns,
    onOpen: details,
    empty: "Documents sent from their Send button show here with their status.",
  });
  drawBar();
  host.append(
    bar,
    table,
    el(
      "div",
      { class: "note" },
      el(
        "p",
        {},
        el("b", {}, "Returned mail"),
        " becomes an Inbox item on the record so someone fixes the address and resends.",
      ),
    ),
  );
}
